/**
 * Vistas do registro fotográfico da assimetria craniana.
 *
 * O núcleo de anexos não interpreta `meta`: a vista é gravada pelo satélite em
 * meta = { "view": "top" } e é sempre opcional (uma foto sem rótulo é válida e
 * aparece como "Sem vista").
 *
 * Funções puras, também consumidas pelo relatório em PDF.
 */

import dayjs from 'dayjs';

export const CRANIAL_PHOTO_CONTEXT = 'cranial_photo';

/** Ordem sugerida de captura: superior, frontal, posterior, laterais. */
export const CRANIAL_PHOTO_VIEWS = [
    { value: 'top', label: 'Superior' },
    { value: 'front', label: 'Frontal' },
    { value: 'back', label: 'Posterior' },
    { value: 'right', label: 'Lateral direita' },
    { value: 'left', label: 'Lateral esquerda' }
];

const VIEW_ORDER = CRANIAL_PHOTO_VIEWS.reduce((map, item, position) => {
    map[item.value] = position;
    return map;
}, {});

/**
 * Rótulo da vista. Sem vista (ou vista desconhecida) vira "Sem vista".
 */
export const getViewLabel = (view) => {
    const found = CRANIAL_PHOTO_VIEWS.find(item => item.value === view);

    return found ? found.label : 'Sem vista';
};

/**
 * Vista gravada no anexo (ou null quando a foto não foi rotulada).
 */
export const getPhotoView = (attachment) => {
    const view = attachment && attachment.meta ? attachment.meta.view : null;

    return view || null;
};

/**
 * Só os anexos do registro fotográfico da assimetria.
 */
export const filterCranialPhotos = (attachments) => {
    return (attachments || []).filter(item => item && item.context === CRANIAL_PHOTO_CONTEXT);
};

const comparePhotos = (a, b) => {
    const orderA = VIEW_ORDER[getPhotoView(a)];
    const orderB = VIEW_ORDER[getPhotoView(b)];
    const positionA = orderA === undefined ? CRANIAL_PHOTO_VIEWS.length : orderA;
    const positionB = orderB === undefined ? CRANIAL_PHOTO_VIEWS.length : orderB;

    if (positionA !== positionB) return positionA - positionB;

    return Number(a.id) - Number(b.id);
};

/**
 * Agrupa as fotos por medição (contextId), já ordenadas por vista e id.
 *
 * @param {Array} photos anexos de context 'cranial_photo'
 * @returns {Object} { [contextId]: Array<anexo> }
 */
export const groupPhotosByEntry = (photos) => {
    const groups = {};

    filterCranialPhotos(photos).forEach((photo) => {
        const key = String(photo.contextId);

        if (!groups[key]) groups[key] = [];

        groups[key].push(photo);
    });

    Object.keys(groups).forEach(key => groups[key].sort(comparePhotos));

    return groups;
};

/**
 * Campos extras do upload de uma foto: só grava `meta` quando há vista.
 */
export const buildCranialPhotoPayload = (value) => {
    const view = value && value.view ? value.view : '';

    return view ? { meta: { view: view } } : {};
};

/** Chave interna do grupo: a vista, ou "none" para as fotos sem vista. */
const getViewKey = (view) => (view ? view : 'none');

/** Vistas na ordem de captura, com o grupo "Sem vista" no fim. */
const GROUP_VIEWS = [...CRANIAL_PHOTO_VIEWS.map(item => item.value), null];

/**
 * Pares antes/depois por vista: a leitura única do registro fotográfico, usada
 * pelo card e pelo relatório em PDF.
 *
 * Por grupo (cada vista, mais o grupo "Sem vista"), `before` é a primeira foto
 * da medição mais antiga que tem foto desse grupo e `current` a primeira foto
 * da medição mais recente. Com uma única medição fotografada, `before` é null e
 * a foto vai para `current`. Grupos sem foto não entram.
 *
 * @param {Array} entries medições do histórico (com id e createdAt)
 * @param {Array} photos anexos de context 'cranial_photo'
 * @returns {Array<{view: string|null, viewLabel: string,
 *                  before: {photo: object, entry: object}|null,
 *                  current: {photo: object, entry: object}}>}
 */
export const buildBeforeAfterPairs = (entries, photos) => {
    const groups = groupPhotosByEntry(photos);

    const ordered = (entries || [])
        .slice()
        .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());

    /** { [chave do grupo]: [{ photo, entry }] } em ordem cronológica */
    const series = {};

    ordered.forEach((entry) => {
        const used = {};

        (groups[String(entry.id)] || []).forEach((photo) => {
            const key = getViewKey(getPhotoView(photo));

            if (used[key]) return;

            used[key] = true;

            if (!series[key]) series[key] = [];

            series[key].push({ photo: photo, entry: entry });
        });
    });

    return GROUP_VIEWS
        .map((view) => {
            const list = series[getViewKey(view)] || [];

            if (list.length === 0) return null;

            return {
                view: view,
                viewLabel: getViewLabel(view),
                before: list.length > 1 ? list[0] : null,
                current: list[list.length - 1]
            };
        })
        .filter(Boolean);
};
